// src/store/storeAdminStore.js
// 店家後台狀態管理
// JWT token 存於 localStorage（key: STORE_ADMIN_TOKEN_KEY）
// 所有 API 呼叫透過 _headers() 自動附加 Authorization: Bearer ...
// 401 回應統一設定 isUnauthorized = true 並清除 token

import { defineStore } from 'pinia'

// ⚠️ MVP 做法：token 存於 localStorage，方便實作但有 XSS 風險。
// 正式版建議改為 httpOnly cookie（需後端配合），或搭配 CSP 降低 XSS 暴露面。
const TOKEN_KEY = 'store_admin_token'

export const useStoreAdminStore = defineStore('storeAdminStore', {
    state: () => ({
        token:          localStorage.getItem(TOKEN_KEY) || null,
        adminInfo:      null,        // JWT payload: { id, store_id, account }
        isUnauthorized: false,

        // ── Dashboard ──────────────────────────────
        todayCount:          0,
        weekCount:           0,
        monthCount:          0,
        pendingCount:        0,
        pendingReservations: [],
        statsLoading:        false,
        statsError:          null,
    }),

    getters: {
        isLoggedIn: (state) => !!state.token,
        storeId:    (state) => state.adminInfo?.store_id ?? null,
    },

    actions: {
        // ── 登入 ──────────────────────────────────
        async login(account, password) {
            try {
                const res  = await fetch('/api/store-admin/login', {
                    method:  'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body:    JSON.stringify({ account, password }),
                })
                const json = await res.json()
                if (json.success) {
                    this.token          = json.token
                    this.isUnauthorized = false
                    localStorage.setItem(TOKEN_KEY, json.token)
                }
                return json
            } catch (e) {
                console.error('storeAdmin login error:', e)
                return { success: false, message: '網路錯誤，請稍後再試' }
            }
        },

        // ── 登出 ──────────────────────────────────
        logout() {
            this.token          = null
            this.adminInfo      = null
            this.isUnauthorized = false
            localStorage.removeItem(TOKEN_KEY)
        },

        // ── 共用 header ────────────────────────────
        _headers() {
            return {
                'Content-Type':  'application/json',
                'Authorization': `Bearer ${this.token}`,
            }
        },

        // ── 401 統一處理 ────────────────────────────
        _handle401() {
            this.isUnauthorized = true
            this.logout()
        },

        // ── 確認 token 有效性（Layout 掛載時呼叫）──
        async fetchMe() {
            if (!this.token) return { success: false }
            try {
                const res = await fetch('/api/store-admin/me', {
                    headers: { 'Authorization': `Bearer ${this.token}` },
                })
                if (res.status === 401) {
                    this._handle401()
                    return { success: false, unauthorized: true }
                }
                const json = await res.json()
                if (json.success) {
                    this.adminInfo      = json.admin
                    this.isUnauthorized = false
                }
                return json
            } catch (e) {
                console.error('fetchMe error:', e)
                return { success: false, message: '網路錯誤' }
            }
        },

        // ── Dashboard 統計 ─────────────────────────
        // 同時撈今日預約與所有 pending 預約
        async fetchDashboardStats() {
            this.statsLoading = true
            this.statsError   = null
            try {
                const r = await fetch('/api/store-admin/dashboard', { headers: this._headers() })
                if (r.status === 401) { this._handle401(); return }
                const j = await r.json()
                if (j.success) {
                    this.todayCount          = j.todayCount
                    this.weekCount           = j.weekCount
                    this.monthCount          = j.monthCount
                    this.pendingCount        = j.pendingCount
                    this.pendingReservations = j.pending
                }
            } catch (e) {
                console.error('fetchDashboardStats error:', e)
                this.statsError = '統計載入失敗'
            } finally {
                this.statsLoading = false
            }
        },

        // ── 老師列表 ────────────────────────────────
        async fetchTeachers() {
            try {
                const res = await fetch('/api/store-admin/teachers', { headers: this._headers() })
                if (res.status === 401) { this._handle401(); return { success: false, unauthorized: true } }
                return res.json()
            } catch (e) {
                return { success: false, message: '網路錯誤' }
            }
        },

        // ── 預約列表（給 ReservationsPage 用）──────
        async fetchReservations({ date = null, date_from = null, date_to = null, status = null } = {}) {
            const params = new URLSearchParams()
            if (date)      params.set('date',      date)
            if (date_from) params.set('date_from', date_from)
            if (date_to)   params.set('date_to',   date_to)
            if (status)    params.set('status',    status)
            try {
                const res = await fetch(`/api/store-admin/reservations?${params}`, {
                    headers: this._headers(),
                })
                if (res.status === 401) { this._handle401(); return { success: false, unauthorized: true } }
                return res.json()
            } catch (e) {
                return { success: false, message: '網路錯誤' }
            }
        },

        // ── 更新預約狀態（狀態機由後端驗證）────────
        async updateReservationStatus(id, status) {
            try {
                const res = await fetch(`/api/store-admin/reservations/${id}`, {
                    method:  'PUT',
                    headers: this._headers(),
                    body:    JSON.stringify({ status }),
                })
                if (res.status === 401) { this._handle401(); return { success: false, unauthorized: true } }
                return res.json()
            } catch (e) {
                return { success: false, message: '網路錯誤' }
            }
        },

        // ── 服務管理 ──────────────────────────────
        async fetchServices() {
            try {
                const res = await fetch('/api/store-admin/services', { headers: this._headers() })
                if (res.status === 401) { this._handle401(); return { success: false, unauthorized: true } }
                return res.json()
            } catch (e) {
                return { success: false, message: '網路錯誤' }
            }
        },
        async createService(payload) {
            try {
                const res = await fetch('/api/store-admin/services', {
                    method: 'POST', headers: this._headers(), body: JSON.stringify(payload),
                })
                if (res.status === 401) { this._handle401(); return { success: false, unauthorized: true } }
                return res.json()
            } catch (e) {
                return { success: false, message: '網路錯誤' }
            }
        },
        async updateService(id, payload) {
            try {
                const res = await fetch(`/api/store-admin/services/${id}`, {
                    method: 'PUT', headers: this._headers(), body: JSON.stringify(payload),
                })
                if (res.status === 401) { this._handle401(); return { success: false, unauthorized: true } }
                return res.json()
            } catch (e) {
                return { success: false, message: '網路錯誤' }
            }
        },
        async deleteService(id) {
            try {
                const res = await fetch(`/api/store-admin/services/${id}`, {
                    method: 'DELETE', headers: this._headers(),
                })
                if (res.status === 401) { this._handle401(); return { success: false, unauthorized: true } }
                return res.json()
            } catch (e) {
                return { success: false, message: '網路錯誤' }
            }
        },

        async deleteReservation(id) {
            try {
                const res = await fetch(`/api/store-admin/reservations/${id}`, {
                    method: 'DELETE', headers: this._headers(),
                })
                if (res.status === 401) { this._handle401(); return { success: false, unauthorized: true } }
                return res.json()
            } catch (e) {
                return { success: false, message: '網路錯誤' }
            }
        },

        // ── 時段管理 ──────────────────────────────
        async fetchSlots(date = null) {
            const params = date ? `?date=${date}` : ''
            try {
                const res = await fetch(`/api/store-admin/slots${params}`, { headers: this._headers() })
                if (res.status === 401) { this._handle401(); return { success: false, unauthorized: true } }
                return res.json()
            } catch (e) {
                return { success: false, message: '網路錯誤' }
            }
        },
        async fetchSlotsRange(date_from, date_to) {
            try {
                const res = await fetch(`/api/store-admin/slots?date_from=${date_from}&date_to=${date_to}`, { headers: this._headers() })
                if (res.status === 401) { this._handle401(); return { success: false, unauthorized: true } }
                return res.json()
            } catch (e) {
                return { success: false, message: '網路錯誤' }
            }
        },
        async deleteSlot(slotId) {
            try {
                const res = await fetch(`/api/store-admin/slots/${slotId}`, {
                    method: 'DELETE', headers: this._headers(),
                })
                if (res.status === 401) { this._handle401(); return { success: false, unauthorized: true } }
                return res.json()
            } catch (e) {
                return { success: false, message: '網路錯誤' }
            }
        },
        async fetchSlotsRangeCheck(startDate, endDate, teacherName = null) {
            try {
                let qs = `?start_date=${startDate}&end_date=${endDate}`
                if (teacherName) qs += `&teacher_name=${encodeURIComponent(teacherName)}`
                const res = await fetch(`/api/store-admin/slots/range-check${qs}`, { headers: this._headers() })
                if (res.status === 401) { this._handle401(); return { success: false, slots: [] } }
                return res.json()
            } catch (e) {
                return { success: false, slots: [] }
            }
        },
        async deleteSlotsByDay(date, teacherName = null) {
            try {
                const qs = teacherName ? `?teacher_name=${encodeURIComponent(teacherName)}` : ''
                const res = await fetch(`/api/store-admin/slots/day/${date}${qs}`, {
                    method: 'DELETE', headers: this._headers(),
                })
                if (res.status === 401) { this._handle401(); return { success: false, unauthorized: true } }
                return res.json()
            } catch (e) {
                return { success: false, message: '網路錯誤' }
            }
        },
        async createSlotsBatch(payload) {
            try {
                const res = await fetch('/api/store-admin/slots/batch', {
                    method: 'POST', headers: this._headers(), body: JSON.stringify(payload),
                })
                if (res.status === 401) { this._handle401(); return { success: false, unauthorized: true } }
                return res.json()
            } catch (e) {
                return { success: false, message: '網路錯誤' }
            }
        },
    },
})
